import React, { useState, useEffect } from 'react';
import { 
    Container, Row, Col, Card, CardHeader, CardBody, 
    Table, Button, Input, Label, UncontrolledDropdown, 
    DropdownToggle, DropdownMenu, DropdownItem, Spinner, Modal, ModalHeader, ModalBody, UncontrolledCarousel 
} from 'reactstrap';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { APIClient } from '../../helpers/api_helper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import BreadCrumb from '../../Components/Common/BreadCrumb';
import UserDetailsModal from '../../Components/Common/UserDetailsModal';
import useUserDetails from '../../hooks/useUserDetails';

const LikeLogs = () => {
    document.title = "Like Logs";
    
    const [likeLogs, setLikeLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        responseType: '',
        dateRange: []
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalCount: 0
    });
    const [sort, setSort] = useState({
        column: '',
        direction: 'asc' // or 'desc'
    });
    const [dateLogsModal, setDateLogsModal] = useState(false);
    const [selectedDateLogs, setSelectedDateLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const {
        userDetailsModal,
        setUserDetailsModal,
        selectedUser,
        loadingUser,
        fetchUserDetails
    } = useUserDetails();

    // Response type options for filter
    const responseTypeOptions = ['like', 'unlike', 'pass', 'all'];

    const fetchLikeLogs = async (page = 1) => {
        try {
            setLoading(true);
            const offset = (page - 1) * pagination.limit;
            let url = `/userResponseLogs?limit=${pagination.limit}&skip=${offset}`;
            
            if (filters.responseType && filters.responseType !== 'all') {
                url += `&responseType=${filters.responseType}`;
            }

            if (filters.dateRange && filters.dateRange.length === 2) {
                const startDate = moment(filters.dateRange[0]).format('DD-MM-YYYY');
                const endDate = moment(filters.dateRange[1]).format('DD-MM-YYYY');
                url += `&startDate=${startDate}&endDate=${endDate}`;
            }
            
            const response = await APIClient.get(url);
            if (response.data && response.data.data) {
                setLikeLogs(response.data.data.matchLogs);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalCount: response.data.data.totalCount
                }));
            }
        } catch (error) {
            console.error('Error fetching like logs:', error);
            toast.error(error.message || 'Failed to fetch like logs', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored"
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLikeLogs(1);
    }, [filters.responseType, filters.dateRange]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            responseType: '',
            dateRange: []
        });
    };

    // Update the date picker styles in useEffect
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .flatpickr-calendar {
                position: fixed !important;
                top: 50% !important;
                left: 50% !important;
                transform: translate(-50%, -50%) !important;
                z-index: 99999 !important;
            }
            .dropdown-menu {
                max-height: none !important;
                overflow: visible !important;
            }
            body.flatpickr-open {
                overflow: hidden;
            }
            
            .carousel {
                background-color: #f8f9fa;
            }
            
            .carousel-item {
                height: 250px;
            }
            
            .carousel-item img {
                height: 100%;
                object-fit: contain;
                margin: auto;
            }
            
            .modal-body {
                max-height: calc(100vh - 180px);
                overflow-y: auto;
            }
            
            .table th {
                width: 40%;
            }

            .cursor-pointer {
                cursor: pointer;
            }

            .cursor-pointer:hover {
                color: #405189 !important;
                text-decoration: underline;
            }

            th .ri-sort-line {
                opacity: 0.3;
            }

            th:hover .ri-sort-line {
                opacity: 1;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const getImageUrl = (url) => {
        if (!url) return '/images/default-avatar.png';
        if (url.startsWith('http')) return url;
        
        const baseUrl = process.env.REACT_APP_MEDIA_URL.replace(/\/$/, '');
        const imagePath = url.replace(/^\//, '');
        return `${baseUrl}/${imagePath}`;
    };

    // Add sortData function
    const sortData = (data, column, direction) => {
        if (!column) return data;

        return [...data].sort((a, b) => {
            let aValue = column.includes('.') ? 
                column.split('.').reduce((obj, key) => obj[key], a) : 
                a[column];
            let bValue = column.includes('.') ? 
                column.split('.').reduce((obj, key) => obj[key], b) : 
                b[column];

            // Handle timestamp/date sorting
            if (column === 'timestamp') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            // Handle strings
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (direction === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });
    };

    // Add handleSort function
    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Update useEffect to sort data when sort changes
    useEffect(() => {
        if (sort.column) {
            setLikeLogs(prev => sortData([...prev], sort.column, sort.direction));
        }
    }, [sort]);

    // Add pagination rendering function
    const renderPagination = () => {
        const totalPages = Math.ceil(pagination.totalCount / pagination.limit);
        
        // Function to get page numbers to show
        const getPageNumbers = () => {
            const delta = 2; // Number of pages to show before and after current page
            const range = [];
            const rangeWithDots = [];
            let l;

            for (let i = 1; i <= totalPages; i++) {
                if (i === 1 || i === totalPages || 
                    (i >= pagination.currentPage - delta && i <= pagination.currentPage + delta)) {
                    range.push(i);
                }
            }

            for (let i of range) {
                if (l) {
                    if (i - l === 2) {
                        rangeWithDots.push(l + 1);
                    } else if (i - l !== 1) {
                        rangeWithDots.push('...');
                    }
                }
                rangeWithDots.push(i);
                l = i;
            }

            return rangeWithDots;
        };

        return (
            <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                <Col sm={4}>
                    <div className="text-muted">Showing <span className="fw-semibold">{likeLogs.length}</span> of <span className="fw-semibold">{pagination.totalCount}</span> Results</div>
                </Col>
                <Col sm={8}>
                    <ul className="pagination pagination-separated justify-content-center justify-content-sm-end mb-sm-0">
                        <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchLikeLogs(pagination.currentPage - 1)}
                                disabled={pagination.currentPage === 1}
                            >
                                <i className="ri-arrow-left-s-line"></i>
                            </Button>
                        </li>
                        
                        {getPageNumbers().map((pageNumber, index) => (
                            <li 
                                key={index} 
                                className={`page-item ${pageNumber === pagination.currentPage ? 'active' : ''} ${pageNumber === '...' ? 'disabled' : ''}`}
                            >
                                <Button
                                    className="page-link"
                                    onClick={() => pageNumber !== '...' && fetchLikeLogs(pageNumber)}
                                    disabled={pageNumber === '...'}
                                >
                                    {pageNumber}
                                </Button>
                            </li>
                        ))}

                        <li className={`page-item ${pagination.currentPage === totalPages ? 'disabled' : ''}`}>
                            <Button
                                className="page-link"
                                onClick={() => fetchLikeLogs(pagination.currentPage + 1)}
                                disabled={pagination.currentPage === totalPages}
                            >
                                <i className="ri-arrow-right-s-line"></i>
                            </Button>
                        </li>
                    </ul>
                </Col>
            </Row>
        );
    };

    return (
        <div className="page-content">
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <Container fluid>
                <BreadCrumb title="Like Logs" pageTitle="Logs" />
                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex align-items-center">
                                <h4 className="card-title mb-0 flex-grow-1">Like Logs</h4>
                                <div className="d-flex gap-2">
                                    {/* Filters */}
                                    <UncontrolledDropdown>
                                        <DropdownToggle tag="button" className="btn btn-soft-info">
                                            <i className="ri-filter-3-line align-bottom"></i> Filter
                                        </DropdownToggle>
                                        <DropdownMenu className="p-3" style={{ minWidth: "300px", maxHeight: "none" }}>
                                            <div className="mb-3">
                                                <Label>Response Type</Label>
                                                <Input 
                                                    type="select" 
                                                    value={filters.responseType}
                                                    onChange={(e) => handleFilterChange('responseType', e.target.value)}
                                                >
                                                    <option value="">All</option>
                                                    {responseTypeOptions.map(type => (
                                                        <option key={type} value={type}>
                                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                                        </option>
                                                    ))}
                                                </Input>
                                            </div>
                                            <div className="mb-3">
                                                <Label>Date Range</Label>
                                                <Flatpickr
                                                    className="form-control"
                                                    options={{
                                                        mode: "range",
                                                        dateFormat: "Y-m-d",
                                                        static: true,
                                                        clickOpens: true,
                                                        closeOnSelect: false,
                                                        allowInput: true,
                                                        onClose: function(selectedDates, dateStr, instance) {
                                                            instance.open();
                                                        },
                                                        position: "auto center"
                                                    }}
                                                    value={filters.dateRange}
                                                    onChange={dates => {
                                                        handleFilterChange('dateRange', dates);
                                                        if (dates.length === 2) {
                                                            document.querySelector('.flatpickr-calendar').style.display = 'none';
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                    <Button 
                                        color="warning" 
                                        onClick={clearFilters}
                                        disabled={!filters.responseType && !filters.dateRange.length}
                                    >
                                        <i className="ri-refresh-line align-bottom"></i> Clear Filters
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardBody>
                                <div className="table-responsive">
                                    <Table className="table-striped table-nowrap align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                {[
                                                    { id: 'slNo', label: 'SL No', width: '60px', sortable: false },
                                                    { id: 'timestamp', label: 'Date & Time', width: '150px', sortable: true },
                                                    { id: 'responseType', label: 'Response Type', sortable: true },
                                                    { id: 'actionByUser.username', label: 'Action By User', sortable: true },
                                                    { id: 'actionOnUser.username', label: 'Action On User', sortable: true },
                                                    { id: 'valueInTokens', label: 'Value in Tokens', sortable: true }
                                                ].map(column => (
                                                    <th 
                                                        key={column.id} 
                                                        style={column.width ? { width: column.width } : {}}
                                                        className={column.sortable ? 'cursor-pointer' : ''}
                                                        onClick={() => column.sortable && handleSort(column.id)}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            {column.label}
                                                            {column.sortable && (
                                                                <span className="ms-2">
                                                                    {sort.column === column.id ? (
                                                                        <i className={`ri-sort-${sort.direction === 'asc' ? 'asc' : 'desc'}`}></i>
                                                                    ) : (
                                                                        <i className="ri-sort-line text-muted"></i>
                                                                    )}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {likeLogs.map((log, index) => (
                                                <tr key={log._id}>
                                                    <td>{index + 1}</td>
                                                    <td>{moment(log.timestamp).format('DD MMM YYYY, hh:mm A')}</td>
                                                    <td>
                                                        <span className={`badge bg-${
                                                            log.responseType === 'like' ? 'success' :
                                                            log.responseType === 'unlike' ? 'danger' :
                                                            'warning'
                                                        }`}>
                                                            {log.responseType.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div 
                                                            className="text-body fw-medium cursor-pointer"
                                                            onClick={() => fetchUserDetails(log.actionByUser.id)}
                                                        >
                                                            {log.actionByUser.username}
                                                            </div>
                                                    </td>
                                                    <td>
                                                        <div 
                                                            className="text-body fw-medium cursor-pointer"
                                                            onClick={() => fetchUserDetails(log.actionOnUser.id)}
                                                        >
                                                            {log.actionOnUser.username}
                                                        </div>
                                                    </td>
                                                    <td>{log.valueInTokens}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            {loading && (
                <div className="overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <Spinner color="success"> Loading... </Spinner>
                </div>
            )}
            <UserDetailsModal 
                isOpen={userDetailsModal}
                toggle={() => setUserDetailsModal(false)}
                user={selectedUser}
                loading={loadingUser}
            />
        </div>
    );
};

export default LikeLogs;